import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { ArrowRight } from "lucide-react";
import { useContent } from "@/context/ContentContext";

export function NewsletterBlock() {
  const { newsletter: n } = useContent();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await api.post("/newsletter", { email });
      toast.success("You're in. Welcome aboard.");
      setEmail("");
    } catch (e) {
      toast.error("Please enter a valid email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="newsletter-block" className="px-6 sm:px-10 py-16">
      <div className="max-w-7xl mx-auto bg-[var(--brand-surface)] rounded-[2.5rem] relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 orange-wash opacity-70 rounded-full blur-2xl" />
        <div className="px-8 sm:px-16 py-16 sm:py-20 grid md:grid-cols-12 gap-10 items-center relative">
          <div className="md:col-span-7">
            <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[var(--brand-primary)] mb-5">
              {n.kicker}
            </p>
            <h2 className="font-display text-5xl sm:text-6xl leading-[1] tracking-tight text-[var(--brand-heading)]">
              {n.heading}
            </h2>
            <p className="mt-6 text-base leading-relaxed text-[var(--brand-muted)] max-w-xl font-medium">
              {n.description}
            </p>
          </div>
          <div className="md:col-span-5">
            <form onSubmit={submit} className="flex flex-col gap-4" data-testid="newsletter-form">
              <input
                data-testid="newsletter-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="bg-[#FBF9F4] text-[var(--brand-heading)] px-5 py-4 rounded-2xl outline-none focus:ring-2 focus:ring-[var(--brand-primary)] placeholder:text-[color-mix(in_srgb,var(--brand-muted)_60%,transparent)] border border-[#E7DFD0]"
                required
              />
              <button
                data-testid="newsletter-submit-button"
                type="submit"
                disabled={loading}
                className="group bg-[var(--brand-primary)] text-[var(--brand-cream)] px-6 py-4 rounded-full font-bold uppercase tracking-[0.1em] text-sm flex items-center justify-center gap-2 transition-all duration-300 hover:bg-[var(--brand-primary-dark)] disabled:opacity-60 shadow-[0_10px_30px_-10px_rgba(225,37,27,0.6)]"
              >
                {loading ? "Subscribing…" : "Subscribe"}
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  const { footer: f, brandName } = useContent();
  const [a, b] = (f.heading || "Let's build something that grows.").split(/\s(?=something)/);
  return (
    <footer data-testid="main-footer" className="bg-[var(--brand-surface)] text-[var(--brand-heading)]">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-24">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <h3 className="font-display text-6xl sm:text-7xl tracking-tight leading-[0.95]">
              {a}<br /><span className="text-[var(--brand-primary)]">{b}</span>
            </h3>
            <Link
              to="/contact"
              data-testid="footer-contact-link"
              className="inline-flex items-center gap-2 mt-8 text-sm font-bold uppercase tracking-[0.12em] text-[var(--brand-heading)] hover:text-[var(--brand-primary)] transition-colors"
            >
              Start a conversation <ArrowRight size={18} />
            </Link>
          </div>
          <div className="md:col-span-3 md:col-start-9">
            <p className="font-mono-brand text-xs uppercase tracking-[0.2em] text-[var(--brand-muted)] mb-5">Explore</p>
            <ul className="space-y-3 font-semibold">
              <li><Link to="/" className="hover:text-[var(--brand-primary)]">Home</Link></li>
              <li><Link to="/about" className="hover:text-[var(--brand-primary)]">About</Link></li>
              <li><Link to="/blog" className="hover:text-[var(--brand-primary)]">AI Journey</Link></li>
              <li><Link to="/resume" data-testid="footer-resume-link" className="hover:text-[var(--brand-primary)]">Resume</Link></li>
              <li><Link to="/contact" className="hover:text-[var(--brand-primary)]">Contact</Link></li>
            </ul>
          </div>
          <div className="md:col-span-2">
            <p className="font-mono-brand text-xs uppercase tracking-[0.2em] text-[var(--brand-muted)] mb-5">Connect</p>
            <ul className="space-y-3 font-semibold">
              <li><a href={f.linkedin} target="_blank" rel="noreferrer" data-testid="footer-linkedin" className="hover:text-[var(--brand-primary)]">LinkedIn</a></li>
              <li><a href={f.instagram} target="_blank" rel="noreferrer" data-testid="footer-instagram" className="hover:text-[var(--brand-primary)]">Instagram</a></li>
              <li><Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-[var(--brand-primary)]">Admin</Link></li>
            </ul>
          </div>
        </div>
        <div className="mt-16 pt-8 border-t border-[#D8CEBE] flex flex-col sm:flex-row justify-between gap-4 text-xs font-bold uppercase tracking-[0.1em] text-[var(--brand-muted)]">
          <p>© {new Date().getFullYear()} {brandName} · Built from 0→1.</p>
          <p className="font-mono-brand normal-case tracking-normal">Insight. Strategy. Growth.</p>
        </div>
      </div>
    </footer>
  );
}
